// §11.1 — what Peek can show, decided from the path alone.
//
// §23 files "richer Peek types" under v1.1, so v1 was deliberately three kinds: an image, text
// (which includes markdown and code — shown as what it is, not rendered), and PDF, whose viewer
// arrives with M13.5. **§34.2 adds a fourth**, `archive`: a zip shows its entry list, which is
// the one thing a person wants to know about one without opening it. **§34.6 adds two more**,
// `audio` and `video` — metadata only, and never playing. Everything else still says so honestly
// rather than rendering bytes as mojibake.
import { extname, type VfsPath } from '@hangar/shared';
// §34.6 (M60.4) — the media table, reached through `assets/**` because §6.5 forbids Explorer
// importing Media and this is the seam it names instead (D-170, D-508).
import { kindOfExtension, mediaExtensions } from '@/assets/media/kinds';

export type PeekKind = 'image' | 'text' | 'pdf' | 'archive' | 'audio' | 'video' | 'none';

const IMAGE_MIME: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.avif': 'image/avif',
  '.bmp': 'image/bmp',
  '.ico': 'image/x-icon',
  '.svg': 'image/svg+xml',
};

/** Text, markdown and code — §11.1's three text-shaped kinds, which all preview the same way. */
const TEXT_EXTENSIONS = new Set([
  '.txt',
  '.md',
  '.markdown',
  '.log',
  '.json',
  '.csv',
  '.tsv',
  '.xml',
  '.yml',
  '.yaml',
  '.toml',
  '.ini',
  '.conf',
  '.env',
  '.ts',
  '.tsx',
  '.js',
  '.jsx',
  '.mjs',
  '.cjs',
  '.css',
  '.scss',
  '.html',
  '.htm',
  '.svelte',
  '.vue',
  '.py',
  '.rb',
  '.go',
  '.rs',
  '.c',
  '.h',
  '.cpp',
  '.cs',
  '.java',
  '.kt',
  '.swift',
  '.sh',
  '.bash',
  '.zsh',
  '.sql',
  '.lua',
  '.php',
]);

export function peekKind(path: VfsPath): PeekKind {
  const ext = extname(path);
  // An SVG is an image, but previewing one means rendering markup the user may not have written.
  // It goes in an `<img>`, which cannot run script, and never inline.
  if (ext in IMAGE_MIME) return 'image';
  if (TEXT_EXTENSIONS.has(ext)) return 'text';
  if (ext === '.pdf') return 'pdf';
  // §34.2 (M56.4) — a zip previews as its own entry list. Reading one means the format module,
  // which Peek reaches through a dynamic import so first paint never pays for a decompressor.
  if (ext === '.zip') return 'archive';
  // §34.6 (M60.4) — audio and video preview as their *metadata*: duration and the codec line, and
  // for a video a single poster frame. Nothing here plays, and `Peek.tsx` is where that is enforced.
  const media = kindOfExtension(ext);
  if (media !== null) return media;
  return 'none';
}

export function imageMime(path: VfsPath): string {
  return IMAGE_MIME[extname(path)] ?? 'application/octet-stream';
}

/** Every extension Peek claims — the fixture the unit test enumerates against. */
export function peekableExtensions(): string[] {
  const media = mediaExtensions();
  return [
    ...Object.keys(IMAGE_MIME),
    ...TEXT_EXTENSIONS,
    '.pdf',
    '.zip',
    ...media.audio,
    ...media.video,
  ].sort();
}
